import { Controller } from "@hotwired/stimulus"
import { formatSubcatLabel, getJSON } from "../api.js"

/**
 * Autocomplete podkategorii z wyszukiwaniem na żywo (GET ?q=).
 *
 * Wariant jednokrotnego wyboru — bez chipów. Po wybraniu podpowiedzi:
 *   - wyświetla „Kategoria › Podkategoria" w inpucie tekstowym,
 *   - zapisuje id do hidden inputu (odczytywanego przez backorder#subscribe),
 *   - zamyka listę.
 *
 * Podpowiedzi pogrupowane wg kategorii: nagłówki grup (ls-combo__group-header)
 * są nieselektywne — klawiatura ↑↓ pomija je, nawigując tylko podkategorie.
 *
 * XSS hardening: zero innerHTML — cały DOM przez createElement/textContent.
 *
 * @target input    - pole tekstowe wyszukiwania
 * @target list     - div na listę podpowiedzi (position: absolute, ls-combo__list)
 * @target hidden   - input type=hidden z wybranym id podkategorii
 * @value  url      - endpoint autocomplete, np. /api/subcategories/
 * @value  maxSuggestions - cap liczby podpowiedzi (domyślnie 8)
 * @value  debounceMs     - opóźnienie debounce w ms (domyślnie 220)
 */
export default class extends Controller {
  static targets = ["input", "list", "hidden"]
  static values = {
    url:            String,
    maxSuggestions: { type: Number,  default: 8 },
    debounceMs:     { type: Number,  default: 220 },
  }

  connect() {
    this._timer   = null   // uchwyt do debounce
    this._hiIndex = -1     // indeks podświetlonej podkategorii (−1 = brak)
    this._items   = []     // bieżące podpowiedzie (WYŁĄCZNIE podkategorie, bez nagłówków)
    this._pickedText = null
  }

  disconnect() {
    clearTimeout(this._timer)
  }

  // Zdarzenie: input — debounce, potem fetch
  search() {
    clearTimeout(this._timer)
    if (this.inputTarget.value !== this._pickedText) {
      this.hiddenTarget.value = ""
      this._pickedText = null
    }
    const q = this.inputTarget.value.trim()
    if (!q) {
      this.#close()
      return
    }
    this._timer = setTimeout(() => this.#fetch(q), this.debounceMsValue)
  }

  // Zdarzenie: keydown — nawigacja klawiaturą (pomija nagłówki grup)
  keydown(e) {
    if (e.key === "Escape") {
      if (this.inputTarget.value !== this._pickedText) this.hiddenTarget.value = ""
      this.#close()
      return
    }
    if (this.listTarget.hidden) return
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this._hiIndex = Math.min(this._hiIndex + 1, this._items.length - 1)
        this.#highlight()
        break
      case "ArrowUp":
        e.preventDefault()
        this._hiIndex = Math.max(this._hiIndex - 1, 0)
        this.#highlight()
        break
      case "Enter":
        e.preventDefault()
        if (this._hiIndex >= 0 && this._items[this._hiIndex]) {
          this.#pick(this._items[this._hiIndex])
        }
        break
    }
  }

  // Zdarzenie: blur — zamknij z opóźnieniem (pozwala obsłużyć mousedown na opcji)
  close() {
    setTimeout(() => this.#close(), 140)
  }

  // --- prywatne ---

  /** Pobiera wyniki z API z parametrem q= (joined with & when url already carries ?) */
  async #fetch(q) {
    const sep = this.urlValue.includes("?") ? "&" : "?"
    const url = `${this.urlValue}${sep}q=${encodeURIComponent(q)}`
    let data
    try {
      data = await getJSON(url)
    } catch {
      this.#close()
      return
    }
    const results = Array.isArray(data) ? data : (data?.results ?? [])
    this._items   = results.slice(0, this.maxSuggestionsValue)
    this.#renderList()
  }

  /**
   * Renderuje listę podpowiedzi pogrupowaną wg kategorii.
   * Nagłówki grup są nieinteraktywne; podkategorie mają indeks do nawigacji klawiaturą.
   * Buduje DOM przez createElement/textContent — zero innerHTML.
   */
  #renderList() {
    // Bezpieczne czyszczenie — textContent zamiast innerHTML
    this.listTarget.textContent = ""

    if (!this._items.length) {
      const empty = document.createElement("div")
      empty.className = "ls-combo__empty"
      empty.textContent = "Brak wyników"
      this.listTarget.appendChild(empty)
      this.listTarget.hidden = false
      return
    }

    // Grupuj wg category_name zachowując kolejność z API
    const groups = new Map()
    for (const item of this._items) {
      const cat = item.category_name ?? "Inne"
      if (!groups.has(cat)) groups.set(cat, [])
      groups.get(cat).push(item)
    }

    this._hiIndex = -1
    let subcatIndex = 0

    for (const [catName, subcats] of groups) {
      // Nagłówek grupy — nieselektywny (aria-hidden, brak mousedown)
      const header = document.createElement("div")
      header.className = "ls-combo__group-header"
      header.setAttribute("aria-hidden", "true")
      header.textContent = catName
      this.listTarget.appendChild(header)

      for (const item of subcats) {
        const row = document.createElement("div")
        row.className = "ls-combo__opt"

        // Wyświetlamy nazwę podkategorii (kategoria w nagłówku grupy)
        const label = document.createElement("span")
        label.className = "ls-combo__opt-label"
        label.textContent = item.name
        row.appendChild(label)

        const idx = subcatIndex  // closure dla eventów
        row.addEventListener("mousedown", (e) => { e.preventDefault(); this.#pick(item) })
        row.addEventListener("mouseenter", () => { this._hiIndex = idx; this.#highlight() })
        this.listTarget.appendChild(row)
        subcatIndex++
      }
    }

    this.listTarget.hidden = false
    this.#highlight()
  }

  /** Podświetla wiersz o indeksie _hiIndex (używa is-hi na ls-combo__opt). */
  #highlight() {
    const opts = this.listTarget.querySelectorAll(".ls-combo__opt")
    opts.forEach((opt, i) => opt.classList.toggle("is-hi", i === this._hiIndex))
  }

  /**
   * Zatwierdza wybór: zapisuje id do hidden inputu,
   * wyświetla „Kategoria › Podkategoria" w polu tekstowym, zamyka listę.
   */
  #pick(item) {
    this.hiddenTarget.value  = item.id
    // Wyświetl „Kategoria › Podkategoria" — ten sam helper co w backorder-controller
    this.inputTarget.value   = formatSubcatLabel(item)
    this._pickedText = this.inputTarget.value
    this.#close()
  }

  /** Zamyka listę i czyści stan nawigacji. */
  #close() {
    this.listTarget.hidden    = true
    this.listTarget.textContent = ""
    this._items   = []
    this._hiIndex = -1
  }
}
